import { Injectable, inject } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { OverlayEventDetail } from '@ionic/core';
import { REFLECTION_MODAL } from '@lyceo/shared/plan/util';
import { TaskReflectionWithNote } from '@lyceo/shared/plan/util-models';
import { IonicComponentRef } from '@lyceo/shared/util-models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, filter, map, of } from 'rxjs';
import { TaskService } from '../../task.service';
import { ReflectionModalActions, TaskActions } from './task.actions';
import { PlanPageActions } from '../plan/plan.actions';
import { ApplicationFacade } from '@lyceo/shared/data-access';
import { PlanFacade } from '../plan/plan.facade';
import { isDefined } from '@lyceo/shared/util';

@Injectable()
export class ReflectionModalEffects {
  private readonly actions$ = inject(Actions);
  private readonly modalController: ModalController = inject(ModalController);
  private readonly taskService: TaskService = inject(TaskService);
  private readonly reflectionModal =
    inject<IonicComponentRef>(REFLECTION_MODAL);
  private readonly applicationFacade: ApplicationFacade =
    inject(ApplicationFacade);
  private readonly planFacade: PlanFacade = inject(PlanFacade);

  public loadTaskFullPupil$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PlanPageActions.pupilCompleteTaskClicked),
      filter(
        (action) =>
          action.value && !!action.task.parentTaskId && action.showReflection,
      ),
      exhaustMap((action) => {
        const pupilId: string | undefined = this.planFacade.pupilId();
        if (!isDefined(pupilId)) {
          return of(
            TaskActions.getFailure({
              error: new Error('Pupil ID is not defined'),
            }),
          );
        }

        return this.taskService.get(pupilId, action.task.id, true).pipe(
          map((task) => TaskActions.getSuccess({ task: task })),
          catchError((error: Error) => of(TaskActions.getFailure({ error }))),
        );
      }),
    );
  });

  public openReflectionModalPupil$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PlanPageActions.pupilCompleteTaskClicked),
      filter(
        (action) =>
          action.value && !!action.task.parentTaskId && action.showReflection,
      ),
      exhaustMap(async (action) => {
        const modal: HTMLIonModalElement = await this.modalController.create({
          component: this.reflectionModal,
          componentProps: {
            task: action.task,
          },
        });
        await modal.present();
        return modal.onDidDismiss();
      }),
      map((data: OverlayEventDetail<TaskReflectionWithNote>) => {
        if (data.data) {
          return ReflectionModalActions.updateReflection({
            request: data.data,
          });
        }

        return ReflectionModalActions.modalClosed();
      }),
    );
  });

  public updateReflection$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReflectionModalActions.updateReflection),
      exhaustMap((action) => {
        const pupilId: string | undefined = this.planFacade.pupilId();
        if (!isDefined(pupilId)) {
          return of(
            ReflectionModalActions.updateReflectionFailure({
              error: new Error('Pupil ID is not defined'),
            }),
          );
        }

        return this.taskService
          .updateReflection(pupilId, action.request.reflection)
          .pipe(
            map((task) =>
              ReflectionModalActions.updateReflectionSuccess({ task }),
            ),
            catchError((error: Error) =>
              of(ReflectionModalActions.updateReflectionFailure({ error })),
            ),
          );
      }),
    );
  });

  public updateNote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReflectionModalActions.updateReflection),
      filter((action) => !!action.request.note),
      exhaustMap((action) =>
        this.taskService.upsertNote(action.request.note).pipe(
          map((note) => ReflectionModalActions.upsertNoteSuccess({ note })),
          catchError((error: Error) =>
            of(ReflectionModalActions.upsertNoteFailure({ error })),
          ),
        ),
      ),
    );
  });

  public updateReflectionFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ReflectionModalActions.updateReflectionFailure),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'error',
            message: 'Het opslaan van de reflectie is niet gelukt.',
          });
        }),
      );
    },
    { dispatch: false },
  );

  public updateNoteFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ReflectionModalActions.upsertNoteFailure),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'error',
            message:
              'Het opslaan van de notitie bij je reflectie is niet gelukt.',
          });
        }),
      );
    },
    { dispatch: false },
  );
}
